import Image from 'common/components/Image';
import { IchiTheme } from 'common/models/theme';
import { AppSyncToken } from 'common/models/token';
import { AppSyncUserInfo } from 'common/models/userInfo';
import { ChainId } from 'constants/constants';
import IchiSwapModalContainer from 'containers/IchiSwapModalContainer';
import StakeModalContainer from 'containers/StakeModalContainer';
import UnstakeModalContainer from 'containers/UnstakeModalContainer';
import { StakeUnstake } from 'enums/stakeUnstake';
import useModal from 'hooks/useModal';
import React, { useEffect } from 'react';
import { usePopper } from 'react-popper';
import { ThemeContext } from 'styled-components';
import { CryptoUtils } from 'utils/cryptoUtils';
import { TokenUtils } from 'utils/tokenUtils';
import FormatFloat from '../FormatPercent/FormatFloat';
import Spinner from '../Spinner';
import { TokenName } from '../TokenIcons/TokenIcon';
import IchiValueRow from './IchiValueRow';
import XIchiRatioValueRow from './XIchiRatioValueRow';
import useSelectedChain from 'hooks/useSelectedChain';

type Props = {
  ichi: AppSyncToken;
  ichiV2: AppSyncToken;
  xichi: AppSyncToken;
  userInfo: AppSyncUserInfo;
  userInfoLoading: boolean;
  refetchUserInfo: () => Promise<void>;
  isOnBottom?: boolean;
  chainId: ChainId;
};

const IchiBalance: React.FC<Props> = (props) => {
  const theme = React.useContext<IchiTheme>(ThemeContext);

  const { chainId } = useSelectedChain();

  const { isShowing: isShowingStakeModal, toggle: toggleStakeModal } =
    useModal();
  const { isShowing: isShowingSwapModal, toggle: toggleSwapModal } = useModal();
  const { isShowing: isShowingUnstakeModal, toggle: toggleUnstakeModal } =
    useModal();
  const [referenceElement, setReferenceElement] = React.useState<unknown>(null);
  const [popperElement, setPopperElement] = React.useState<unknown>(null);
  const {
    styles,
    attributes,
    forceUpdate: popperUpdate,
  } = usePopper(referenceElement as Element, popperElement as HTMLElement, {
    placement: props.isOnBottom ? 'top' : 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: props.isOnBottom ? [-10, 10] : [0, 8],
        },
      },
    ],
  });
  const [isActive, setIsActive] = React.useState(false);
  // const [isLoading, setIsLoading] = React.useState(false);
  // const ichiAddress = CryptoUtils.getEtherscanLink(props.ichi.chainId, props.ichi.address, 'address') || '';
  // const ichiV2Address = CryptoUtils.getEtherscanLink(props.ichiV2.chainId, props.ichiV2.address, 'address') || '';
  // const xIchiAddress = CryptoUtils.getEtherscanLink(props.xichi.chainId, props.xichi.address, 'address') || '';

  useEffect(() => {
    // close popper when clicked out
    const handleOnClick = (ev) => {
      if (
        !(ev.composedPath() as Array<HTMLElement>).includes(
          popperElement as HTMLElement
        ) &&
        !(ev.composedPath() as Array<HTMLElement>).includes(
          referenceElement as HTMLElement
        )
      ) {
        setIsActive(false);
      }
    };
    document.addEventListener('click', handleOnClick);
    return () => document.removeEventListener('click', handleOnClick);
  });

  const setPopperIsActive = (isA: boolean) => {
    if (popperUpdate) {
      popperUpdate();
    }
    setIsActive(isA);
  };

  const v1Token = TokenUtils.getTokenByName(props.userInfo, 'ichi');
  const v2Token = TokenUtils.getTokenByName(props.userInfo, 'ichi_v2');
  const v1Amount = v1Token.balance;
  const v2Amount = v2Token.balance;
  const isIchiUpgradable = !!v1Token.address && !!v2Token.address;
  const legacyXIchiAmount = TokenUtils.getTokenByName(
    props.userInfo,
    'xichi'
  ).balance;

  const renderRows = () => {
    return (
      <>
        <IchiValueRow
          token={props.ichiV2}
          icon={TokenName.ICHI_V2}
          name="ICHI"
          xichi={props.xichi}
          type="v2"
          userInfo={props.userInfo}
          chainId={props.chainId}
        />
        {v1Amount > 0 && (
          <IchiValueRow
            token={props.ichi}
            icon={TokenName.ICHI_LEGACY}
            name="Legacy ICHI"
            xichi={props.xichi}
            type="legacy"
            userInfo={props.userInfo}
            chainId={props.chainId}
          />
        )}
        {legacyXIchiAmount > 0 && (
          <>
            <IchiValueRow
              token={props.ichi}
              icon={TokenName.XICHI_LEGACY}
              name="xICHI"
              xichi={props.xichi}
              type="xIchiLegacy"
              userInfo={props.userInfo}
              chainId={props.chainId}
            />
            <XIchiRatioValueRow
              ichi={props.ichi}
              xichi={props.xichi}
              icon={TokenName.XICHI_LEGACY_ICHI_LEGACY}
            />
          </>
        )}
      </>
    );
  };

  const showStakeUnstake =
    !CryptoUtils.isPolygon(chainId) && legacyXIchiAmount > 0;

  return (
    <>
      <button
        id="ichi-balance"
        className="ichi-balance-button-bg-color flex flex-row justify-between items-center px-3 h-10 rounded-[10px] white-color ml-3 font-medium"
        type="button"
        onClick={() => setPopperIsActive(!isActive)}
        ref={setReferenceElement}
      >
        <Image src={theme.icons.ichiWhite} alt="ICHI Owned" />
        <div className="min-w-[82px]">
          {props.userInfoLoading ? (
            <Spinner isLoading={props.userInfoLoading} />
          ) : (
            <FormatFloat
              value={(v1Amount + v2Amount).toString()}
              trunc={false}
            />
          )}
        </div>
        <img
          src="../../assets/image/chevron-down-white.svg"
          alt="expand"
          className="w-3"
        />
      </button>

      <div
        className={`ichi-balance-popover ichi-balance-bg-color w-full sm:w-[460px] z-[1020] rounded-[10px] absolute right-full shadow-[0_2px_50px_0_rgba(0,0,0,0.25)] ${
          isActive ? 'visible' : 'invisible'
        }`}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
      >
        <div className="flex flex-col overflow-hidden rounded-[10px]">
          {/* Header */}
          <div className="content-inner-inner-bg-color p-[20px] border-b border-gray-300">
            <div className="flex flex-row justify-between items-center">
              <div className="flex flex-row items-center ichi-balance-header-text-color font-medium text-xl">
                <img
                  src={theme.icons.ichiOwned}
                  alt="ICHI icon"
                  className="mr-3"
                />
                ICHI Price
              </div>
              <div className="flex flex-col items-end">
                <FormatFloat
                  // eslint-disable-next-line @typescript-eslint/dot-notation
                  value={TokenUtils.getIchiStats(
                    props.ichiV2,
                    props.xichi
                  ).ichiPrice.toString()}
                  prefix="$"
                  trunc={false}
                  className="ichi-balance-header-text-color font-medium text-xl e2e-ichi-price"
                />
                {/* TODO: Is this the correct math here */}
                <FormatFloat
                  // eslint-disable-next-line @typescript-eslint/dot-notation
                  value={TokenUtils.getIchiStats(
                    props.ichiV2,
                    props.xichi
                  ).ichiPriceChange.toString()}
                  postfix="%"
                  trunc={false}
                  className="font-medium text-sm sub-text-color"
                />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-[15px]">
            <div className="heading-color font-medium text-2xl my-2 sm:my-[20px] text-center">
              Your ICHI Balance
            </div>
            {renderRows()}
            {isIchiUpgradable && (
              <div className="text-center">
                <a
                  className="primary-color cursor-pointer font-semibold underline"
                  onClick={toggleSwapModal}
                >
                  Upgrade ICHI
                </a>
              </div>
            )}
          </div>

          {/* Footer */}
          {showStakeUnstake && (
            <div className="footer-bg-color p-[20px] h-24 flex flex-row justify-evenly items-center">
              <button
                className="primary-button-bg-color cursor-pointer h-10 w-40 rounded-[10px] font-semibold"
                type="button"
                onClick={toggleStakeModal}
              >
                <span className="primary-button-color">Stake</span>
              </button>
              <button
                className="primary-button-bg-color cursor-pointer h-10 w-40 rounded-[10px] font-semibold"
                type="button"
                onClick={toggleUnstakeModal}
              >
                <span className="primary-button-color">Unstake</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {!CryptoUtils.isPolygon(chainId) && isShowingSwapModal && (
        <IchiSwapModalContainer
          key="swap"
          isShowing={isShowingSwapModal}
          toggle={toggleSwapModal}
          refetchUserInfo={() => void props.refetchUserInfo()}
          userInfo={props.userInfo}
          ichi={props.ichi}
          ichiV2={props.ichiV2}
          xichi={props.xichi}
        />
      )}
      {showStakeUnstake && isShowingStakeModal && (
        <StakeModalContainer
          key={StakeUnstake.STAKE}
          isShowing={isShowingStakeModal}
          toggle={toggleStakeModal}
          refetchUserInfo={props.refetchUserInfo}
          userInfo={props.userInfo}
          ichi={props.ichi}
          xichi={props.xichi}
        />
      )}
      {showStakeUnstake && isShowingUnstakeModal && (
        <UnstakeModalContainer
          key={StakeUnstake.UNSTAKE}
          isShowing={isShowingUnstakeModal}
          toggle={toggleUnstakeModal}
          refetchUserInfo={props.refetchUserInfo}
          userInfo={props.userInfo}
          ichi={props.ichi}
          xichi={props.xichi}
        />
      )}
    </>
  );
};

export default IchiBalance;
